import React, { useState, useEffect } from 'react';
import {
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Select,
    MenuItem,
    Snackbar
} from '@mui/material';

const Skills = ({ characterData, updateCharacterData, classData }) => {
    const { level, realm, className } = characterData;
    const [skills, setSkills] = useState({});
    const [spells, setSpells] = useState({});
    const [skillFactor, setSkillFactor] = useState(null);
    const [skillPoints, setSkillPoints] = useState(characterData.skillPoints || { total: 0, used: 0, remaining: 0 });
    const [pointsPerSkill, setPointsPerSkill] = useState(characterData.skills || {});
    const [hoveredSkill, setHoveredSkill] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // useEffect(() => {
    //     if (realm && className) {
    //         loadSkillsAndFactor();
    //     }
    // }, [realm, className]);

    useEffect(() => {
        if (classData && classData['skill factor'] !== undefined) {
            const loadedSkills = classData.skills || {};
            const loadedSpells = classData.spells || {};
            const skillFactor = classData['skill factor'];

            setSkills(loadedSkills);
            setSpells(loadedSpells);
            setSkillFactor(skillFactor);

            if (Object.keys(characterData.skills).length === 0) {
                const combined = {
                    ...loadedSkills,
                    ...Object.keys(loadedSpells).reduce((acc, cur) => ({ ...acc, [cur]: [] }), {})
                };
                resetPoints(combined, characterData.level, skillFactor);
            } else {
                setPointsPerSkill(characterData.skills);
                setSkillPoints(characterData.skillPoints || { total: 0, used: 0, remaining: 0 });
            }
        }
    }, [classData, characterData.level, characterData.skills, characterData.skillPoints]);


    useEffect(() => {
        if (skillFactor !== null) {
            const totalPoints = calculatePointsForLevel(level, skillFactor);
            const initialUsedPoints = calculateUsedPoints(pointsPerSkill);
            const remainingPoints = totalPoints - initialUsedPoints;
            const updatedSkillPoints = {
                total: totalPoints,
                used: initialUsedPoints,
                remaining: remainingPoints,
            };
            setSkillPoints(updatedSkillPoints);
            updateCharacterData({ skills: pointsPerSkill, skillPoints: updatedSkillPoints });
        }
    }, [level, skillFactor, pointsPerSkill]);


    const resetPoints = (loadedSkills, level, skillFactor) => {
        const initialPoints = {};
        Object.keys(loadedSkills).forEach(skill => {
            initialPoints[skill] = 1;
        });
        setPointsPerSkill(initialPoints);
        const totalPoints = calculatePointsForLevel(level, skillFactor);
        const initialUsedPoints = calculateUsedPoints(initialPoints);
        const remainingPoints = totalPoints - initialUsedPoints;
        const newSkillPoints = {
            total: totalPoints,
            used: initialUsedPoints,
            remaining: remainingPoints,
        };
        setSkillPoints(newSkillPoints);
        updateCharacterData({ skills: initialPoints, skillPoints: newSkillPoints });
    };

    const handleSkillPointsChange = (skillName, newValue) => {
        const parsedValue = parseInt(newValue, 10) || 0;
        if (parsedValue < 1 || parsedValue > 50) return;
        if (parsedValue > level) return openSnackbar(`You cannot assign more points than your character's level (${level}).`);
        const currentPoints = pointsPerSkill[skillName] || 1;
        const pointDifference = parsedValue - currentPoints;
        const potentialRemainingPoints = skillPoints.remaining - pointDifference;
        if (potentialRemainingPoints < 0) return openSnackbar("You don't have enough remaining points to assign.");
        const updatedPointsPerSkill = { ...pointsPerSkill, [skillName]: parsedValue };
        setPointsPerSkill(updatedPointsPerSkill);
        const updatedUsedPoints = calculateUsedPoints(updatedPointsPerSkill);
        const remainingPoints = skillPoints.total - updatedUsedPoints;
        const newSkillPoints = {
            total: skillPoints.total,
            used: updatedUsedPoints,
            remaining: remainingPoints,
        };
        setSkillPoints(newSkillPoints);
        updateCharacterData({ skills: updatedPointsPerSkill, skillPoints: newSkillPoints });
    };

    const calculatePointsForLevel = (level, factor) => {
        let total = 0;
        for (let i = 2; i <= 5 && i <= level; i++) total += i;
        for (let i = 6; i <= level; i++) total += Math.floor(i * factor);
        for (let i = 40.5; i <= 49.5 && i <= level; i += 1) total += Math.floor(i * factor / 2);
        return total;
    };

    const calculateUsedPoints = (pointsPerSkill) => {
        let used = 0;
        for (const points of Object.values(pointsPerSkill)) {
            for (let i = 2; i <= points; i++) used += i;
        }
        return used;
    };

    const openSnackbar = (message) => setSnackbarOpen({ open: true, message });
    const handleCloseSnackbar = () => setSnackbarOpen(false);

    const compactRowSx = { height: '32px', padding: '2px 8px' };

    const columnWidths = [
        { width: '25%' },
        { width: '55%' },
        { width: '20%' },
    ];

    const renderSpellTrainingRows = () => {
        return Object.entries(spells).flatMap(([lineName, lineData], idx, arr) => {
            const rows = [];

            rows.push(
                <TableRow key={`${lineName}-header`} sx={{ ...compactRowSx, ...(idx !== 0 ? { borderTop: '4px solid #ccc' } : {}) }}>
                    <TableCell sx={{ ...columnWidths[0], ...compactRowSx, color: 'primary.main', fontWeight: 'bold' }}>{lineName}</TableCell>
                    <TableCell sx={{ ...columnWidths[1], ...compactRowSx }}></TableCell>
                    <TableCell sx={{ ...columnWidths[2], ...compactRowSx }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Button size="small" onClick={() => handleSkillPointsChange(lineName, (pointsPerSkill[lineName] || 1) - 1)} disabled={(pointsPerSkill[lineName] || 1) <= 1}>-</Button>
                            <Select
                                value={pointsPerSkill[lineName] || 1}
                                onChange={(e) => handleSkillPointsChange(lineName, e.target.value)}
                                size="small"
                                sx={{ width: '75px' }}
                            >
                                {Array.from({ length: 50 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>)}
                            </Select>
                            <Button size="small" onClick={() => handleSkillPointsChange(lineName, (pointsPerSkill[lineName] || 1) + 1)}>+</Button>
                        </Box>
                    </TableCell>
                </TableRow>
            );

            ['base', 'spec'].forEach((typeKey) => {
                const section = lineData[typeKey];
                if (!section || !section.spells?.length) return;
                const grouped = section.spells.reduce((acc, s) => {
                    const key = s.type || 'Misc';
                    acc[key] = acc[key] || [];
                    acc[key].push(s);
                    return acc;
                }, {});

                rows.push(
                    <TableRow key={`${lineName}-${typeKey}`} sx={{ backgroundColor: 'primary.main', ...compactRowSx }}>
                        <TableCell colSpan={3} sx={{ color: 'black', ...compactRowSx }}><strong>{section.name}</strong></TableCell>
                    </TableRow>
                );

                Object.entries(grouped).forEach(([group, spells]) => {
                    rows.push(
                        <TableRow key={`${lineName}-${typeKey}-${group}`} sx={compactRowSx}>
                            <TableCell sx={{ ...columnWidths[0], ...compactRowSx }}><strong>{group}</strong></TableCell>
                            <TableCell colSpan={2} sx={{ ...columnWidths[1], ...compactRowSx }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {spells.map((s, i) => (
                                        <Button
                                            key={i}
                                            size="small"
                                            sx={{ minWidth: '24px', height: '24px', padding: 0,
                                                backgroundColor: (pointsPerSkill[lineName] || 1) >= s.level ? 'primary.main' : '',
                                                color: (pointsPerSkill[lineName] || 1) >= s.level ? 'background.paper' : '',
                                                ':hover': { backgroundColor: (pointsPerSkill[lineName] || 1) >= s.level ? 'primary.dark' : '' }
                                            }}
                                            title={`[${s.level}] ${s.name} (${s.type})`}
                                            onMouseEnter={() => setHoveredSkill(s)}
                                            onMouseLeave={() => setHoveredSkill(null)}
                                        >{s.level}</Button>
                                    ))}
                                </Box>
                            </TableCell>
                        </TableRow>
                    );
                });
            });

            return rows;
        });
    };

    return (
        <Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={12} md={8}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><Button variant="contained" onClick={() => resetPoints({ ...skills, ...Object.keys(spells).reduce((acc, cur) => ({ ...acc, [cur]: [] }), {}) })}>Reset</Button></TableCell>
                                <TableCell>Levels</TableCell>
                                <TableCell>Points</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(skills).map(([skillName, levels]) => (
                                <TableRow key={skillName}>
                                    <TableCell>{skillName}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {levels.map((s, i) => (
                                                <Button
                                                    key={i}
                                                    size="small"
                                                    onClick={() => handleSkillPointsChange(skillName, s.level)}
                                                    disabled={skillPoints.remaining < calculateUsedPoints({ [skillName]: s.level }) - calculateUsedPoints({ [skillName]: pointsPerSkill[skillName] || 1 })}
                                                    onMouseEnter={() => setHoveredSkill(s)}
                                                    onMouseLeave={() => setHoveredSkill(null)}
                                                    sx={{ minWidth: '24px', height: '24px', padding: 0,
                                                        backgroundColor: (pointsPerSkill[skillName] || 1) >= s.level ? 'primary.main' : '',
                                                        color: (pointsPerSkill[skillName] || 1) >= s.level ? 'white' : ''
                                                    }}
                                                >{s.level}</Button>
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Button size="small" onClick={() => handleSkillPointsChange(skillName, (pointsPerSkill[skillName] || 1) - 1)} disabled={(pointsPerSkill[skillName] || 1) <= 1}>-</Button>
                                            <Select
                                                value={pointsPerSkill[skillName] || 1}
                                                onChange={(e) => handleSkillPointsChange(skillName, e.target.value)}
                                                size="small"
                                                sx={{ width: '75px' }}
                                            >
                                                {Array.from({ length: 50 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>)}
                                            </Select>
                                            <Button size="small" onClick={() => handleSkillPointsChange(skillName, (pointsPerSkill[skillName] || 1) + 1)}>+</Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {renderSpellTrainingRows()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6">Skill Points</Typography>
                    <Typography>Total: {skillPoints.total}</Typography>
                    <Typography>Used: {skillPoints.used}</Typography>
                    <Typography>Remaining: {skillPoints.remaining}</Typography>
                </Paper>
                {hoveredSkill && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="subtitle1">[{hoveredSkill.level}] {hoveredSkill.name}</Typography>
                        {hoveredSkill.opening && <Typography>Opening: {hoveredSkill.opening}</Typography>}
                        {hoveredSkill.followup && <Typography>Follow-up: {hoveredSkill.followup}</Typography>}
                        {hoveredSkill.endurance && <Typography>Endurance: {hoveredSkill.endurance}</Typography>}
                        {hoveredSkill.damage && <Typography>Damage: {hoveredSkill.damage}</Typography>}
                        {hoveredSkill.hit && <Typography>Hit: {hoveredSkill.hit}</Typography>}
                        {hoveredSkill.defense && <Typography>Defense: {hoveredSkill.defense}</Typography>}
                        {hoveredSkill.description && <Typography>Description: {hoveredSkill.description}</Typography>}
                        {hoveredSkill.type && <Typography>Type: {hoveredSkill.type}</Typography>}
                        {hoveredSkill.delve && <Typography>Delve: {hoveredSkill.delve}</Typography>}
                        {hoveredSkill.notes && <Typography>Notes: {hoveredSkill.notes}</Typography>}
                    </Paper>
                )}
            </Grid>

            <Snackbar
                open={snackbarOpen.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message={snackbarOpen.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Grid>
    );
};

export default Skills;
